import { type SupabaseClient } from "@supabase/supabase-js";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge class names
 * @param inputs - The class names to merge
 * @returns The merged class names
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Get domain information based on hostname
 * @param hostname - The hostname from the request
 * @returns Object containing subdomain and whether it's a subdomain request
 */
export function getDomainInfo(hostname: string) {
  const isDev = process.env.NODE_ENV === "development";
  const appEnv = process.env.NEXT_PUBLIC_APP_ENV || "development";
  const isPreprod = appEnv === "preprod";

  const PROD_DOMAIN = process.env.NEXT_PUBLIC_ROOT_DOMAIN || "renwu.app";
  const PRE_PROD_DOMAIN = process.env.NEXT_PUBLIC_DEV_DOMAIN || "renwu.dev";
  const LOCAL_DOMAIN = process.env.NEXT_PUBLIC_LOCAL_DOMAIN || "localhost:3000";

  const currentDomain = isDev ? LOCAL_DOMAIN : isPreprod ? PRE_PROD_DOMAIN : PROD_DOMAIN;

  // Extract subdomain for all environments
  const subdomain = hostname.split(".")[0];

  const isSubdomainRequest = subdomain !== "" && hostname !== currentDomain;

  return {
    subdomain,
    isSubdomainRequest,
    currentDomain,
  };
}

/**
 * Check if the URL is a subdomain
 * @param url - The URL to check
 * @returns True if the URL is a subdomain, false otherwise
 */
export function isSubdomain(url: string) {
  const regex = new RegExp(/^([a-z]+\:\/{2})?([\w-]+\.[\w-]+\.\w+)$/);

  return !!url.match(regex);
}

/**
 * Go to the main domain
 * @param path - The path to go to
 * @returns The URL to the main domain
 */
export function goToMainDomain(path: string) {
  const isDev = process.env.NODE_ENV === "development";
  const appEnv = process.env.NEXT_PUBLIC_APP_ENV || "development";
  const isPreprod = appEnv === "preprod";

  let domain;
  if (isDev) {
    domain = process.env.NEXT_PUBLIC_LOCAL_DOMAIN || "localhost:3000";
  } else if (isPreprod) {
    domain = process.env.NEXT_PUBLIC_DEV_DOMAIN || "renwu.dev";
  } else {
    domain = process.env.NEXT_PUBLIC_ROOT_DOMAIN || "renwu.app";
  }

  // Ensure path doesn't start with a slash when concatenating
  const cleanPath = path.startsWith("/") ? path.substring(1) : path;

  return `${isDev ? "http" : "https"}://${domain}/${cleanPath}`;
}

/**
 * Go to the subdomain
 * @param subdomain - The subdomain to go to
 * @param path - The path to go to
 * @returns The URL to the subdomain
 */
export function goToSubdomain(subdomain: string, path: string) {
  const isDev = process.env.NODE_ENV === "development";
  const appEnv = process.env.NEXT_PUBLIC_APP_ENV || "development";
  const isPreprod = appEnv === "preprod";

  let domain;
  if (isDev) {
    domain = process.env.NEXT_PUBLIC_LOCAL_DOMAIN || "localhost:3000";
  } else if (isPreprod) {
    domain = process.env.NEXT_PUBLIC_DEV_DOMAIN || "renwu.dev";
  } else {
    domain = process.env.NEXT_PUBLIC_ROOT_DOMAIN || "renwu.app";
  }

  if (!subdomain) {
    return goToMainDomain(path);
  }

  // Ensure path doesn't start with a slash when concatenating
  const cleanPath = path.startsWith("/") ? path.substring(1) : path;

  return `${isDev ? "http" : "https"}://${subdomain}.${domain}/${cleanPath}`;
}

/**
 * Generate a slug
 * @param name - The name to generate a slug from
 * @returns The slug
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase() // Convert to lowercase
    .trim() // Remove leading and trailing whitespace
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/[\s_-]+/g, "-") // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens
}

/**
 * Ensure a slug is unique
 * @param supabase - The Supabase client
 * @param table - The table to check
 * @param slug - The slug to check
 * @returns The unique slug
 */
export async function ensureUniqueSlug(
  supabase: SupabaseClient,
  table: string,
  slug: string
): Promise<string> {
  // Check if slug exists
  const { data, error } = await supabase.from(table).select("slug").eq("slug", slug).limit(1);

  if (error) throw error;

  // If no matching slug found, return the original
  if (!data || data.length === 0) return slug;

  // Otherwise, find a unique slug by adding a number
  let counter = 1;
  let newSlug = `${slug}-${counter}`;

  let isUnique = false;
  while (!isUnique) {
    const { data, error } = await supabase.from(table).select("slug").eq("slug", newSlug).limit(1);

    if (error) throw error;

    if (!data || data.length === 0) {
      isUnique = true;
    } else {
      counter++;
      newSlug = `${slug}-${counter}`;
    }
  }

  return newSlug;
}

/**
 * Build a dynamic URL with cluster and project slugs
 * @param baseUrl - The base URL (e.g., "/dashboard")
 * @param params - The route parameters (clusterSlug and projectSlug)
 * @returns The complete URL
 */
export function buildDynamicUrl(
  baseUrl: string,
  params?: { clusterSlug?: string | null; projectSlug?: string | null }
) {
  // Skip processing for special URLs like /home or external URLs
  if (baseUrl.startsWith("http") || baseUrl.startsWith("/home") || baseUrl.startsWith("#")) {
    return baseUrl;
  }

  // If we don't have cluster and project slugs, return the base URL
  if (!params?.clusterSlug || !params?.projectSlug) {
    return baseUrl;
  }

  // Build the dynamic URL
  return `/${params.clusterSlug}/${params.projectSlug}${
    baseUrl.startsWith("/") ? baseUrl : `/${baseUrl}`
  }`;
}
